import { CognitoUser, CognitoUserSession, UserData } from "amazon-cognito-identity-js";
import { FormEvent, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { AppContext } from "../../contexts/AppContext";
import { AuthContext } from "../../contexts/AuthContext";
import { User } from "../../types";
import { ErrorText } from "../../components/Errors";
import { Button } from "../components/Button";
import { logError } from "../../utils";

// Rollout gate: while true, only staff accounts see the MFA settings
export const MFA_STAFF_ONLY = true;
const STAFF_DOMAIN = "@sherpah.com.au";

const TOTP_CODE_REGEX = /^\d{6}$/;

export function canManageMfa(user: User | null | undefined) {
    if (!user || !(user.is_doctor || user.is_operator || user.is_superuser)) {
        return false;
    }
    return !MFA_STAFF_ONLY || user.email.toLowerCase().endsWith(STAFF_DOMAIN);
}

export function normaliseTotpCode(code: string) {
    return code.replace(/\s/g, "");
}

function isTotpEnabled(userData: UserData) {
    return (userData.UserMFASettingList || []).includes("SOFTWARE_TOKEN_MFA");
}

function fetchUserData(user: CognitoUser): Promise<UserData> {
    return new Promise((resolve, reject) => {
        user.getUserData((err, data) => err || !data ? reject(err || new Error("No user data")) : resolve(data), { bypassCache: true });
    });
}

/**
 * The Cognito user for the signed-in Django user, or null when they signed in through the
 * legacy (Django password) login and therefore have no Cognito session to manage MFA with.
 */
function useCognitoUserFor(user: User | null) {
    const { userPool } = useContext(AppContext);
    const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>();

    useEffect(() => {
        const current = userPool?.getCurrentUser();
        if (!current || !user) {
            setCognitoUser(null);
            return;
        }
        current.getSession((err: Error | null, session: CognitoUserSession | null) => {
            const sessionEmail = session?.getIdToken().payload.email;
            if (err || !session?.isValid() || typeof sessionEmail !== "string" || sessionEmail.toLowerCase() !== user.email.toLowerCase()) {
                setCognitoUser(null);
            } else {
                setCognitoUser(current);
            }
        });
    }, [userPool, user]);

    return cognitoUser;
}

export default function MfaSettings() {
    const authContext = useContext(AuthContext)!;
    const user = authContext.authState.user;
    const navigate = useNavigate();
    const eligible = canManageMfa(user);
    const cognitoUser = useCognitoUserFor(eligible ? user : null);

    if (!eligible || cognitoUser === undefined) {
        return null;
    }

    if (cognitoUser === null) {
        return <MfaSection>
            <p>To set up two-factor authentication, sign out and sign in again using the new sign-in page.</p>
            <Button variant="secondary" text="Sign out and sign in again" onClick={async () => {
                await authContext.logout();
                navigate("/accounts/login2");
            }} />
        </MfaSection>;
    }

    return <MfaPanel cognitoUser={cognitoUser} email={user!.email} />;
}

function MfaSection({ children }: React.PropsWithChildren) {
    return <div className="mfa-settings">
        <h3>Two-factor authentication</h3>
        {children}
    </div>;
}

enum PanelState {
    loading,
    off,
    on,
    setup,
    confirmDisable
}

export function MfaPanel({ cognitoUser, email }: { cognitoUser: CognitoUser, email: string }) {
    const [state, setState] = useState(PanelState.loading);
    const [secret, setSecret] = useState<string>();
    const [qrCodeSvg, setQrCodeSvg] = useState<string>();
    const [code, setCode] = useState("");
    const [message, setMessage] = useState<string>();
    const [error, setError] = useState<string>();
    const [busy, setBusy] = useState(false);

    const fail = (err: unknown) => {
        logError(err);
        setError(err instanceof Error ? err.message : "Something went wrong, please try again");
        setBusy(false);
    };

    const refreshStatus = () => fetchUserData(cognitoUser)
        .then(data => setState(isTotpEnabled(data) ? PanelState.on : PanelState.off))
        .catch(fail);

    useEffect(() => {
        refreshStatus();
    }, [cognitoUser]);

    const onStartSetup = () => {
        setError(undefined);
        setMessage(undefined);
        setBusy(true);
        cognitoUser.associateSoftwareToken({
            associateSecretCode: async (secretCode) => {
                const uri = `otpauth://totp/Sherpah:${encodeURIComponent(email)}?secret=${secretCode}&issuer=Sherpah`;
                try {
                    setQrCodeSvg(await QRCode.toString(uri, { type: "svg", margin: 1 }));
                } catch (err) {
                    // The secret can still be entered manually
                    logError(err);
                }
                setSecret(secretCode);
                setCode("");
                setState(PanelState.setup);
                setBusy(false);
            },
            onFailure: fail
        });
    };

    const onVerify = (e: FormEvent) => {
        e.preventDefault();
        setError(undefined);
        const userCode = normaliseTotpCode(code);
        if (!TOTP_CODE_REGEX.test(userCode)) {
            setError("Enter the 6-digit code from your authenticator app");
            return;
        }
        setBusy(true);
        cognitoUser.verifySoftwareToken(userCode, "Authenticator app", {
            onSuccess: () => {
                cognitoUser.setUserMfaPreference(null, { Enabled: true, PreferredMfa: true }, (err) => {
                    if (err) {
                        return fail(err);
                    }
                    setSecret(undefined);
                    setQrCodeSvg(undefined);
                    setMessage("Two-factor authentication is on. You'll be asked for a code from your authenticator app each time you sign in.");
                    setBusy(false);
                    refreshStatus();
                });
            },
            onFailure: (err) => {
                fail(err.name === "EnableSoftwareTokenMFAException" || err.name === "CodeMismatchException"
                    ? new Error("That code didn't match. Check your authenticator app and try again.")
                    : err);
            }
        });
    };

    const onDisable = () => {
        setError(undefined);
        setMessage(undefined);
        setBusy(true);
        cognitoUser.setUserMfaPreference(null, { Enabled: false, PreferredMfa: false }, (err) => {
            if (err) {
                return fail(err);
            }
            setMessage("Two-factor authentication is off.");
            setBusy(false);
            refreshStatus();
        });
    };

    let content: React.ReactNode;
    switch (state) {
        case PanelState.loading:
            content = <p>Loading...</p>;
            break;

        case PanelState.off:
            content = <>
                <p>Status: <b>Off</b></p>
                <p>Protect your account by requiring a code from an authenticator app (such as Google Authenticator or Microsoft Authenticator) when you sign in.</p>
                <Button text="Set up two-factor authentication" disabled={busy} onClick={onStartSetup} />
            </>;
            break;

        case PanelState.on:
            content = <>
                <p>Status: <b>On</b></p>
                <Button variant="secondary" text="Turn off two-factor authentication" disabled={busy} onClick={() => {
                    setMessage(undefined);
                    setState(PanelState.confirmDisable);
                }} />
            </>;
            break;

        case PanelState.confirmDisable:
            content = <>
                <p>Turn off two-factor authentication? You'll only need your password to sign in.</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button text="Turn off" disabled={busy} onClick={onDisable} />
                    <Button variant="secondary" text="Cancel" disabled={busy} onClick={() => setState(PanelState.on)} />
                </div>
            </>;
            break;

        case PanelState.setup:
            content = <form onSubmit={onVerify}>
                <p>1. Scan this QR code with your authenticator app.</p>
                {qrCodeSvg && <img alt="QR code for your authenticator app" style={{ width: 200, maxWidth: "100%" }}
                    src={`data:image/svg+xml;utf8,${encodeURIComponent(qrCodeSvg)}`} />}
                <p>Can't scan it? Enter this key instead: <code data-testid="mfa-secret">{secret?.match(/.{1,4}/g)?.join(" ")}</code></p>
                <div className="form-question">
                    <label htmlFor="mfa-setup-code">2. Enter the 6-digit code from the app</label>
                    <input id="mfa-setup-code" inputMode="numeric" autoComplete="one-time-code" maxLength={7}
                        value={code} onChange={e => setCode(e.target.value)} />
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button text="Turn on" disabled={busy} />
                    <Button variant="secondary" text="Cancel" disabled={busy} onClick={(e) => {
                        e.preventDefault();
                        setError(undefined);
                        setState(PanelState.off);
                    }} />
                </div>
            </form>;
            break;
    }

    return <MfaSection>
        {content}
        {message && <p>{message}</p>}
        {error && <ErrorText>{error}</ErrorText>}
    </MfaSection>;
}
